import { useState } from 'react';

import type { TypeSelectOption } from '@thefarmersfront/kitchen';

import { collapseLineBreaks, limitLength, removeEmoji } from '~/utils/common';

import {
  DELIVERY_AGENCY_DELIVERY_LOCATION_DETAIL_REQUIRED_CODES,
  DELIVERY_AGENCY_DELIVERY_LOCATION_OPTIONS,
  DELIVERY_AGENCY_ENTRANCE_METHOD_DETAIL_REQUIRED_CODES,
  DELIVERY_AGENCY_ENTRANCE_METHOD_OPTIONS,
  DELIVERY_AGENCY_LOCATION_DETAIL_MAX_LENGTH,
} from '~/constants';

import useAutoFocusOnRequired from './useAutoFocusOnRequired';

import type {
  TypeDeliveryAgencyCode,
  TypeDeliveryAgencyOrderDetail,
  TypePutOrdersOrderIdParams,
} from '~/types/delivery-agency.types';

/** 유효성 통과 시 반환되는 주문 수정 payload (orderId 제외 4개 필드) */
type TypeOrderUpdatePayload = Omit<TypePutOrdersOrderIdParams, 'orderId'>;

/** 옵션 상수에서 code enum 타입을 파생한 Select 옵션 타입 (canonical 옵션만 표현) */
type TypeDeliveryLocationOption = (typeof DELIVERY_AGENCY_DELIVERY_LOCATION_OPTIONS)[number] | null;
type TypeEntranceMethodOption = (typeof DELIVERY_AGENCY_ENTRANCE_METHOD_OPTIONS)[number] | null;

type TypeOrderDetailFormErrors = {
  deliveryLocation: boolean;
  deliveryLocationDetail: boolean;
  entranceMethod: boolean;
  entranceMethodDetail: boolean;
};

const INITIAL_ERRORS: TypeOrderDetailFormErrors = {
  deliveryLocation: false,
  deliveryLocationDetail: false,
  entranceMethod: false,
  entranceMethodDetail: false,
};

const toSelectOption = <T extends string>(
  source: TypeDeliveryAgencyCode,
  options: readonly TypeSelectOption<T>[]
): TypeSelectOption<T> | null => options.find((option) => option.value === source.code) ?? null;

const isDetailRequired = <T extends string>(location: TypeSelectOption<T> | null, requiredCodes: readonly T[]) =>
  location !== null && requiredCodes.includes(location.value);

/**
 * 상세 텍스트를 전송 형태로 정규화한다. dirty 판정·payload가 같은 기준을 공유한다.
 * 줄바꿈은 공백으로 치환하되 앞뒤·내부 공백은 보존하고, 공백뿐이거나 빈 값은 null로 만든다.
 */
const normalizeDetail = (value: string): string | null => {
  const collapsed = collapseLineBreaks(value);
  return collapsed.trim() === '' ? null : collapsed;
};

/** 상세 입력값 정제(이모지 제거 → 줄바꿈 공백 치환 → 최대 길이 절단). 수령장소/출입방법 상세가 같은 규칙을 공유한다. */
const sanitizeDetailInput = (value: string) =>
  limitLength(collapseLineBreaks(removeEmoji(value)), DELIVERY_AGENCY_LOCATION_DETAIL_MAX_LENGTH);

/**
 * [배송 주문 상세] 수령장소/출입방법 편집 폼 상태 훅
 *
 * - 상세 조회 데이터로 폼 값을 초기화한다.
 * - 수령장소/출입방법 선택값에 따라 상세 필수 여부를 실시간 파생한다.
 * - 상세 필수 상태에서 옵션 선택 시 해당 상세 입력란으로 커서를 자동 이동한다(커서는 텍스트 끝).
 * - 선택값이 바뀌면 이전 선택 기준으로 입력한 상세 입력값을 초기화한다.
 * - 상세는 사용자 입력(onChange)에만 정제를 적용한다: 이모지 제거, 줄바꿈 공백 치환(단일 라인 정책), 최대 100자 절단. 서버 초기값은 그대로 둔다.
 */
const useDeliveryAgencyOrderDetailForm = (data: TypeDeliveryAgencyOrderDetail) => {
  const { orderInfoOutput } = data;

  const [initialValues] = useState(() => ({
    deliveryLocation: toSelectOption(orderInfoOutput.deliveryLocation, DELIVERY_AGENCY_DELIVERY_LOCATION_OPTIONS),
    deliveryLocationDetail: orderInfoOutput.deliveryLocationDetail ?? '',
    entranceMethod: toSelectOption(orderInfoOutput.entranceMethod, DELIVERY_AGENCY_ENTRANCE_METHOD_OPTIONS),
    entranceMethodDetail: orderInfoOutput.entranceMethodDetail ?? '',
  }));

  const [deliveryLocation, setDeliveryLocation] = useState<TypeDeliveryLocationOption>(initialValues.deliveryLocation);
  const [deliveryLocationDetail, setDeliveryLocationDetail] = useState(initialValues.deliveryLocationDetail);
  const [entranceMethod, setEntranceMethod] = useState<TypeEntranceMethodOption>(initialValues.entranceMethod);
  const [entranceMethodDetail, setEntranceMethodDetail] = useState(initialValues.entranceMethodDetail);

  const [errors, setErrors] = useState<TypeOrderDetailFormErrors>(INITIAL_ERRORS);

  const isDeliveryLocationDetailRequired = isDetailRequired(
    deliveryLocation,
    DELIVERY_AGENCY_DELIVERY_LOCATION_DETAIL_REQUIRED_CODES
  );
  const isEntranceMethodDetailRequired = isDetailRequired(
    entranceMethod,
    DELIVERY_AGENCY_ENTRANCE_METHOD_DETAIL_REQUIRED_CODES
  );

  // 상세가 필수인 상태에서 옵션을 선택할 때마다 해당 상세 입력란으로 커서를 자동 이동한다.
  // (동일 값 재선택 포함. kitchen Select 포커스 복귀와의 레이스 회피는 useAutoFocusOnRequired 참고)
  const { ref: deliveryLocationDetailRef, notifySelection: notifyDeliveryLocationSelection } = useAutoFocusOnRequired(
    isDeliveryLocationDetailRequired
  );
  const { ref: entranceMethodDetailRef, notifySelection: notifyEntranceMethodSelection } =
    useAutoFocusOnRequired(isEntranceMethodDetailRequired);

  // kitchen Select는 선택값을 넓은 타입(string)으로 넘기므로, 넘어온 값을 직접 쓰지 않고
  // canonical 옵션 상수에서 enum 타입 옵션을 다시 찾아 저장한다. (없으면 null)
  const handleChangeDeliveryLocation = (_: unknown, value: TypeSelectOption<string>) => {
    const option = DELIVERY_AGENCY_DELIVERY_LOCATION_OPTIONS.find((o) => o.value === value.value) ?? null;
    // 선택값이 실제로 바뀌면 이전 선택 기준으로 입력한 상세는 무효이므로 초기화한다(동일 값 재선택은 보존).
    if (option?.value !== deliveryLocation?.value) {
      setDeliveryLocationDetail('');
    }
    setDeliveryLocation(option);
    setErrors((prev) => ({ ...prev, deliveryLocation: false, deliveryLocationDetail: false }));
    notifyDeliveryLocationSelection();
  };

  const handleChangeEntranceMethod = (_: unknown, value: TypeSelectOption<string>) => {
    const option = DELIVERY_AGENCY_ENTRANCE_METHOD_OPTIONS.find((o) => o.value === value.value) ?? null;
    if (option?.value !== entranceMethod?.value) {
      setEntranceMethodDetail('');
    }
    setEntranceMethod(option);
    setErrors((prev) => ({ ...prev, entranceMethod: false, entranceMethodDetail: false }));
    notifyEntranceMethodSelection();
  };

  const handleChangeDeliveryLocationDetail = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDeliveryLocationDetail(sanitizeDetailInput(event.target.value));
    setErrors((prev) => ({ ...prev, deliveryLocationDetail: false }));
  };

  const handleChangeEntranceMethodDetail = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEntranceMethodDetail(sanitizeDetailInput(event.target.value));
    setErrors((prev) => ({ ...prev, entranceMethodDetail: false }));
  };

  // blur 시 표시값을 전송 기준(normalizeDetail)으로 맞춘다. 앞뒤 공백은 보존하고 공백뿐인 값만 비운다.
  const handleBlurDeliveryLocationDetail = () => setDeliveryLocationDetail((prev) => normalizeDetail(prev) ?? '');
  const handleBlurEntranceMethodDetail = () => setEntranceMethodDetail((prev) => normalizeDetail(prev) ?? '');

  // dirty 판정도 normalizeDetail 기준으로 비교한다. 앞뒤 공백도 전송되므로 공백을 덧붙이면 변경으로 본다.
  const isDirty =
    deliveryLocation?.value !== initialValues.deliveryLocation?.value ||
    normalizeDetail(deliveryLocationDetail) !== normalizeDetail(initialValues.deliveryLocationDetail) ||
    entranceMethod?.value !== initialValues.entranceMethod?.value ||
    normalizeDetail(entranceMethodDetail) !== normalizeDetail(initialValues.entranceMethodDetail);

  /**
   * 유효성 검사 후 에러 상태를 갱신한다.
   * 통과 시 주문 수정 payload(상세는 `normalizeDetail`로 정규화 — 앞뒤 공백 제거 후 빈 값은 null)를, 실패 시 null을 반환한다.
   */
  const validate = (): TypeOrderUpdatePayload | null => {
    const deliveryLocationDetailError = isDeliveryLocationDetailRequired && deliveryLocationDetail.trim() === '';
    const entranceMethodDetailError = isEntranceMethodDetailRequired && entranceMethodDetail.trim() === '';

    setErrors({
      deliveryLocation: deliveryLocation === null,
      entranceMethod: entranceMethod === null,
      deliveryLocationDetail: deliveryLocationDetailError,
      entranceMethodDetail: entranceMethodDetailError,
    });

    if (
      deliveryLocation === null ||
      entranceMethod === null ||
      deliveryLocationDetailError ||
      entranceMethodDetailError
    ) {
      return null;
    }

    return {
      deliveryLocation: deliveryLocation.value,
      deliveryLocationDetail: normalizeDetail(deliveryLocationDetail),
      entranceMethod: entranceMethod.value,
      entranceMethodDetail: normalizeDetail(entranceMethodDetail),
    };
  };

  return {
    values: { deliveryLocation, deliveryLocationDetail, entranceMethod, entranceMethodDetail },
    errors,
    isDeliveryLocationDetailRequired,
    isEntranceMethodDetailRequired,
    isDirty,
    deliveryLocationDetailRef,
    entranceMethodDetailRef,
    handleChangeDeliveryLocation,
    handleChangeEntranceMethod,
    handleChangeDeliveryLocationDetail,
    handleChangeEntranceMethodDetail,
    handleBlurDeliveryLocationDetail,
    handleBlurEntranceMethodDetail,
    validate,
  };
};

export default useDeliveryAgencyOrderDetailForm;
